import React from 'react';
import { useSubscriptionStatus } from './useSubscriptionStatus';
import { SUBSCRIPTION_STATUS } from '../utils/constants';

// Constants for plans that have export access
const EXPORT_ENABLED_PLANS = ['appMax', 'procurementPro'];

/**
 * Hook to determine if the current user has export permissions
 * @param {Object} subscription - subscription object from redux store
 * @returns {Object} - export permission status and helper functions
 */
export const useExportPermission = (subscription) => {
  const { pending, data } = useSubscriptionStatus(subscription);

  const hasValidSubscription = React.useMemo(() => {
    return [SUBSCRIPTION_STATUS.SUBSCRIBED, SUBSCRIPTION_STATUS.ACTIVE].includes(data?.status);
  }, [data?.status]);

  const hasExportAccess = React.useMemo(() => {
    if (!hasValidSubscription) return false;
    return EXPORT_ENABLED_PLANS.includes(data?.currentPlan);
  }, [hasValidSubscription, data?.currentPlan]);

  const isLoading = pending;

  // Tooltip message for users without export access
  const tooltipMessage =
    'Upgrade to Procurement Pro today to unlock the export feature! Contact your Registration and Contracting Specialist to get started.';

  return {
    hasExportAccess,
    isLoading,
    currentPlan: data?.currentPlan,
    subscriptionStatus: data?.status,
    tooltipMessage,
    // Helper function to wrap export actions
    withExportPermission: (callback, fallback) => {
      return (...args) => {
        if (isLoading) return;

        if (hasExportAccess) {
          return callback?.(...args);
        }

        // If no export access, execute fallback (like showing tooltip)
        return fallback?.(...args);
      };
    },
  };
};
